/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * EOS Overview Component
 *
 * Migrated from: lego/page/eos/overview/Overview.jsx (953 lines)
 *
 * Main page with 3 graph panels (Study / Country / Site level).
 * Each panel has cascading Study → Country → Site filter dropdowns
 * and a doughnut chart showing EOS completion status.
 *
 * Key differences from ETMF Overview:
 *   - 3 graphs (no "All Levels" panel)
 *   - No generate/update study flows
 *   - EOS-specific APIs (eosdashboard, eos/internal/studies, etc.)
 *   - Summary ↔ Detailed toggle for non-legacy mode
 *   - isLegacy prop drives dual-mode behavior (EOS 2.0 vs 3.0)
 */

import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Popover, OverlayTrigger } from 'react-bootstrap'
import cloneDeep from 'lodash/cloneDeep'
import sortBy from 'lodash/sortBy'
import values from 'lodash/values'
import isEqual from 'lodash/isEqual'
import { useServiceContext } from '../../../root'
import { useNavigateWithParams } from '../../../hooks/useNavigateWithParams'
import { eosApiService } from '../../../services/api/api.eos'
import { EOS_ACTIVITY, EOS_ACKNOWLEDGEMENT_STATUS } from '../../../constants/eos'
import { Select } from '../../../components/Select/Select'
import persistService from '../../../services/persist'
import EosStatusGraph from './status-graph/EosStatusGraph'
import EosStatusGraphToggle from './status-graph/EosStatusGraphToggle'
import EosStatusDocuments from './status-documents/EosStatusDocuments'
import type {
  EosFilterState,
  EosFilteredOptions,
  EosDataState,
  EosGraphData,
  EosSelectOption,
  EosStatusFilter,
  EosDocumentsFilterData,
  EosAssignedStudy,
  EosSiteInfo,
  EosCacheData,
  EosStatusResponse,
  EosGraphInfoItem
} from './types'

// Simple label component matching legacy ControlLabel
const ControlLabel: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <label className={`control-label ${className || ''}`}>{children}</label>
)

// Property name keys
const propsLow = {
  study: 'study',
  country: 'country',
  site: 'site'
} as const

const orderedPropsLow = values(propsLow)

// Section level keys
const LEVEL = {
  trial: 'studyLevel',
  country: 'countryLevel',
  site: 'siteLevel'
} as const

const sections = values(LEVEL)

interface EosOverviewProps {
  isLegacy?: boolean
}

const EosOverview: React.FC<EosOverviewProps> = ({ isLegacy }) => {
  const { t } = useTranslation()
  const context = useServiceContext()
  const navigate = useNavigateWithParams()
  const Constants = (context as unknown as Record<string, unknown>).constant as Record<string, Record<string, unknown>>

  const LOCAL_STORAGE_KEY = 'eosOverview' + (isLegacy ? '' : '_3.0')
  const versionName = '0.01'

  // Activity options depending on legacy vs 3.0
  const activities: EosSelectOption[] = [
    { value: EOS_ACTIVITY.SUMMARY, label: t('EOS_ACTIVITY_SUMMARY') },
    ...(isLegacy
      ? [
          { value: EOS_ACTIVITY.DOWNLOAD, label: t('EOS_ACTIVITY_DOWNLOAD') },
          { value: EOS_ACTIVITY.ACKNOWLEDGEMENT, label: t('EOS_ACTIVITY_ACKNOWLEDGEMENT') }
        ]
      : [{ value: EOS_ACTIVITY.DETAILED, label: t('EOS_ACTIVITY_DETAILED') }])
  ]

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [data, setData] = useState<EosDataState>({
    studyLevel: null,
    countryLevel: null,
    siteLevel: null
  })

  const [studyLevelData, setStudyLevelData] = useState<EosGraphData | null>(null)
  const [countryLevelData, setCountryLevelData] = useState<EosGraphData | null>(null)
  const [siteLevelData, setSiteLevelData] = useState<EosGraphData | null>(null)

  const [filteredSelectsOptions, setFilteredSelectsOptions] = useState<EosFilteredOptions>({
    studyLevel: {},
    countryLevel: {},
    siteLevel: {}
  })

  const [filter, setFilter] = useState<EosFilterState>({
    studyLevel: { type: 'STUDY', activity: EOS_ACTIVITY.SUMMARY },
    countryLevel: { type: 'COUNTRY', activity: EOS_ACTIVITY.SUMMARY },
    siteLevel: { type: 'SITE', activity: EOS_ACTIVITY.SUMMARY }
  })

  const [eosDocumentsFilter, setEosDocumentsFilter] = useState<EosDocumentsFilterData>({})

  // ---------------------------------------------------------------------------
  // Tooltip
  // ---------------------------------------------------------------------------

  const popoverReference = (
    <Popover id="goto-folder-popup-info">
      <div dangerouslySetInnerHTML={{ __html: t('ETMF_TOOLTIP_GO_TO_PARENT_FOLDER') }}></div>
    </Popover>
  )

  // ---------------------------------------------------------------------------
  // Initialization
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const cachedData = persistService.getBasic<EosCacheData>(LOCAL_STORAGE_KEY)

    if (cachedData?.version === versionName && cachedData?.filter) {
      const filterData = cloneDeep(filter)
      const cachedFilter = cachedData.filter

      Object.keys(cachedFilter).forEach((key) => {
        const sectionKey = key as keyof EosFilterState
        if (cachedFilter[sectionKey]) {
          Object.assign(filterData[sectionKey], cachedFilter[sectionKey])
        }
      })

      Object.keys(filterData).forEach((key) => {
        filterData[key as keyof EosFilterState].activity = EOS_ACTIVITY.SUMMARY
      })

      setFilter(filterData)
      reloadAllSections(true, filterData)
    } else {
      reloadAllSections(true)
    }
  }, [])

  // ---------------------------------------------------------------------------
  // Graph click handlers
  // ---------------------------------------------------------------------------

  const onGraphSummaryClick = (activityType: string, levelFilter: EosStatusFilter) => {
    const clickData: EosDocumentsFilterData = {
      status: 'ALL',
      model: isLegacy ? { ...cloneDeep(levelFilter), activity: activityType } : cloneDeep(levelFilter),
      isGraphLabelClick: true
    }
    setEosDocumentsFilter(clickData)
  }

  const onGraphLabelClick = (status: string, levelFilter: EosStatusFilter) => {
    const clickData: EosDocumentsFilterData = {
      status,
      model: levelFilter,
      isGraphLabelClick: true
    }
    setEosDocumentsFilter(clickData)
  }

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const reloadAllSections = async (initialLoad: boolean, filterData: EosFilterState = cloneDeep(filter)) => {
    try {
      await downloadStudies()
      await Promise.all([
        reloadSection(sections[0], undefined, false, true, filterData),
        reloadSection(sections[1], undefined, false, true, filterData),
        reloadSection(sections[2], undefined, false, true, filterData)
      ])

      const isSilent = !initialLoad
      await Promise.all([
        loadEosStatus(sections[0], isSilent, filterData),
        loadEosStatus(sections[1], isSilent, filterData),
        loadEosStatus(sections[2], isSilent, filterData)
      ])

      storeCache(filterData)
    } catch (error) {
      console.error('[EosOverview] reloadAllSections error:', error)
    }
  }

  const downloadStudies = async (): Promise<void> => {
    const studies = await eosApiService.getAssignedStudies()
    const converted = convertServerValues(studies as unknown as EosAssignedStudy[], propsLow.study)

    setFilteredSelectsOptions((prev) => {
      const updated = cloneDeep(prev)
      Object.keys(updated).forEach((key) => {
        const sectionKey = key as keyof EosFilteredOptions
        updated[sectionKey].study = converted
      })
      return updated
    })
  }

  const convertServerValues = (
    input: EosAssignedStudy[] | string[] | EosSiteInfo[],
    type: string
  ): EosSelectOption[] => {
    const output: EosSelectOption[] = []

    switch (type) {
      case propsLow.study:
        ;(input as EosAssignedStudy[])
          .filter((item) => !(isLegacy ? !item.legacy : item.legacy) && !item.archived)
          .forEach((item) => {
            output.push({ value: item.studyFolderId, label: item.studyName })
          })
        break
      case propsLow.country:
        ;(input as string[]).forEach((item) => {
          output.push({ value: item, label: item })
        })
        break
      case propsLow.site:
        ;(input as EosSiteInfo[]).forEach((item) => {
          output.push({ value: item.siteFolderId, label: item.siteName })
        })
        break
    }

    return sortBy(output, (item) => item.label.toLowerCase())
  }

  // ---------------------------------------------------------------------------
  // Section reload logic
  // ---------------------------------------------------------------------------

  const reloadSection = async (
    type: string,
    name?: string,
    clear?: boolean,
    silent?: boolean,
    filterData: EosFilterState = cloneDeep(filter)
  ): Promise<EosFilterState> => {
    switch (type) {
      case 'studyLevel':
        return reloadSectionStudyLevel(name, filterData)
      case 'countryLevel':
        return reloadSectionCountryLevel(name, clear, filterData)
      case 'siteLevel':
        return reloadSectionSiteLevel(name, clear, filterData)
      default:
        return filterData
    }
  }

  const reloadSectionStudyLevel = async (
    whatWasChanged?: string,
    filterData: EosFilterState = cloneDeep(filter)
  ): Promise<EosFilterState> => {
    const sectionFilter = filterData.studyLevel
    const reloadOptions = !whatWasChanged

    if (whatWasChanged === propsLow.study || reloadOptions) {
      if (sectionFilter.study && !sectionFilter.activity) {
        sectionFilter.activity = activities[0].value as string
      }
      setFilter(cloneDeep(filterData))
    }

    return filterData
  }

  const reloadSectionCountryLevel = async (
    whatWasChanged?: string,
    clear?: boolean,
    filterData: EosFilterState = cloneDeep(filter)
  ): Promise<EosFilterState> => {
    const sectionFilter = filterData.countryLevel
    const reloadOptions = !whatWasChanged

    if (whatWasChanged === propsLow.study || reloadOptions) {
      if (clear) {
        clearProps(propsLow.country, sectionFilter)
      }

      if (sectionFilter.study) {
        const results = (await eosApiService.getCountriesUnderStudy(sectionFilter.study.value as number)) as string[]
        const countryOptions = convertServerValues(results, propsLow.country)
        countryOptions.unshift({ label: t('ETMF_LABEL_SELECT_ALL_COUNTRIES'), value: null })

        setFilteredSelectsOptions((prev) => ({
          ...prev,
          countryLevel: { ...prev.countryLevel, country: countryOptions }
        }))

        if (countryOptions.length === 1) {
          sectionFilter.country = countryOptions[0]
        }

        setFilter(cloneDeep(filterData))
      }
    }

    if (whatWasChanged === propsLow.country || reloadOptions) {
      if (sectionFilter.country && sectionFilter.study) {
        if (!sectionFilter.activity) {
          sectionFilter.activity = activities[0].value as string
          setFilter(cloneDeep(filterData))
        }
      }
    }

    return filterData
  }

  const reloadSectionSiteLevel = async (
    whatWasChanged?: string,
    clear?: boolean,
    filterData: EosFilterState = cloneDeep(filter)
  ): Promise<EosFilterState> => {
    const sectionFilter = filterData.siteLevel

    const reloadAll = !whatWasChanged

    if (whatWasChanged === propsLow.study || reloadAll) {
      if (clear) {
        clearProps(propsLow.country, sectionFilter)
      }

      if (sectionFilter.study) {
        const results = (await eosApiService.getCountriesUnderStudy(sectionFilter.study.value as number)) as string[]
        const countryOptions = convertServerValues(results, propsLow.country)
        countryOptions.unshift({ label: t('ETMF_LABEL_SELECT_ALL_COUNTRIES'), value: null })

        setFilteredSelectsOptions((prev) => ({
          ...prev,
          siteLevel: { ...prev.siteLevel, country: countryOptions }
        }))

        if (countryOptions.length === 1) {
          sectionFilter.country = countryOptions[0]
          await reloadSection('siteLevel', propsLow.country, clear, undefined, filterData)
        }

        setFilter(cloneDeep(filterData))
      }
    }

    if (whatWasChanged === propsLow.country || reloadAll) {
      if (clear) {
        clearProps(propsLow.site, sectionFilter)
      }

      if (sectionFilter.study && sectionFilter.country) {
        const results = (await eosApiService.getSitesUnderStudy(sectionFilter.study.value as number, [
          sectionFilter.country.value as string
        ])) as EosSiteInfo[]
        const siteOptions = convertServerValues(results, propsLow.site)
        siteOptions.unshift({ label: t('ETMF_LABEL_SELECT_ALL_SITES'), value: null })

        setFilteredSelectsOptions((prev) => ({
          ...prev,
          siteLevel: { ...prev.siteLevel, site: siteOptions }
        }))

        if (siteOptions.length === 1) {
          sectionFilter.site = siteOptions[0]
        }

        setFilter(cloneDeep(filterData))
      }
    }

    if (whatWasChanged === propsLow.site || reloadAll) {
      if (sectionFilter.site && !sectionFilter.activity) {
        sectionFilter.activity = activities[0].value as string
      }
    }

    return filterData
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const clearProps = (skipUntilProp: string, propsToChange: EosStatusFilter) => {
    let skip = true
    const record = propsToChange as unknown as Record<string, unknown>
    orderedPropsLow.forEach((propName) => {
      if (propName === skipUntilProp) {
        skip = false
      }
      if (!skip && record[propName]) {
        record[propName] = null
      }
    })
  }

  // ---------------------------------------------------------------------------
  // Load EOS Status (graph data)
  // ---------------------------------------------------------------------------

  const loadEosStatus = async (
    level: string,
    isSilent: boolean,
    filterData: EosFilterState = cloneDeep(filter)
  ): Promise<void> => {
    const sectionFilter = filterData[level as keyof EosFilterState]

    const levelFilter = (() => {
      switch (level) {
        case 'studyLevel':
          if (sectionFilter.study) {
            return { studyFolderId: sectionFilter.study.value as number }
          }
          return null
        case 'countryLevel':
          if (sectionFilter.study && sectionFilter.country) {
            return {
              studyFolderId: sectionFilter.study.value as number,
              countries: sectionFilter.country.value as string
            }
          }
          return null
        case 'siteLevel':
          if (sectionFilter.study && sectionFilter.country && sectionFilter.site) {
            return {
              studyFolderId: sectionFilter.study.value as number,
              countries: sectionFilter.country.value as string,
              siteFolderIds: sectionFilter.site.value as number
            }
          }
          return null
      }
    })()

    if (levelFilter && sectionFilter.activity) {
      const filterCopy = cloneDeep(filterData[level as keyof EosFilterState])

      const activityValue =
        typeof sectionFilter.activity === 'object'
          ? (sectionFilter.activity as EosSelectOption).value
          : sectionFilter.activity

      const activityTypes =
        activityValue === EOS_ACTIVITY.DOWNLOAD || activityValue === EOS_ACTIVITY.ACKNOWLEDGEMENT
          ? [activityValue as string]
          : [EOS_ACTIVITY.SUMMARY]

      try {
        const status = (await eosApiService.getEosStatus({
          ...levelFilter,
          activityTypes: activityTypes.join(','),
          level: sectionFilter.type,
          filterOnlyStudyFolder: false
        })) as EosStatusResponse

        if (isEqual(filterData[level as keyof EosFilterState], filterCopy)) {
          const downloadCompl = Math.round((status.downloadedResourcesCount / status.totalResourceCount) * 100) || 0
          const ackCompl = status.acknowledgementStats
            ? Math.round((status.acknowledgementStats.completed / status.totalResourceCount) * 100)
            : 0

          const graphData: EosGraphData | null = (() => {
            switch (activityValue) {
              case EOS_ACTIVITY.DOWNLOAD:
                return {
                  graphData: [
                    {
                      label: t('EOS_DOWNLOAD_STATUS_NOT_OCCURRED'),
                      status: 'NOT_OCCURRED',
                      data: status.totalResourceCount - status.downloadedResourcesCount,
                      color: '#D1D8E0'
                    },
                    {
                      label: t('EOS_DOWNLOAD_STATUS_OCCURRED'),
                      status: 'OCCURRED',
                      data: status.downloadedResourcesCount,
                      color: '#37BE87'
                    }
                  ],
                  totalData: {
                    data: downloadCompl + '%',
                    label: t('COMPLETION'),
                    inGraphLabel: t('COMPLETE')
                  }
                }
              case EOS_ACTIVITY.ACKNOWLEDGEMENT:
                return {
                  graphData: [
                    {
                      label: t('EOS_COMPLETION_TYPE_NOT_STARTED'),
                      status: 'NOT_STARTED',
                      data:
                        status.totalResourceCount -
                        (status.acknowledgementStats?.completed ?? 0) -
                        (status.acknowledgementStats?.inProgress ?? 0),
                      color: '#D1D8E0'
                    },
                    {
                      label: t('ETMF_COMPLETION_TYPE_IN_PROGRESS_DOCUMENTS'),
                      status: 'IN_PROGRESS',
                      data: status.acknowledgementStats?.inProgress ?? 0,
                      color: '#F2BE4A'
                    },
                    {
                      label: t('ETMF_GRAPHS_LABEL_COMPLETE'),
                      status: 'COMPLETED',
                      data: status.acknowledgementStats?.completed ?? 0,
                      color: '#37BE87'
                    }
                  ],
                  totalData: {
                    data: ackCompl + '%',
                    label: t('COMPLETION'),
                    inGraphLabel: t('COMPLETE')
                  }
                }
              case EOS_ACTIVITY.DETAILED:
                return {
                  graphData: [
                    {
                      label: t('EOS_COMPLETION_TYPE_NOT_STARTED'),
                      status: 'NOT_STARTED',
                      data:
                        status.totalResourceCount -
                        (status.downloadInProgress +
                          (status.acknowledgementStats?.inProgress ?? 0) +
                          (status.acknowledgementStats?.completed ?? 0)),
                      color: '#D1D8E0'
                    },
                    {
                      label: t('EOS_ACTIVITY_DOWNLOAD'),
                      status: 'DOWNLOAD',
                      data: status.downloadInProgress,
                      color: '#F2BE4A'
                    },
                    {
                      label: t('EOS_ACTIVITY_ACKNOWLEDGEMENT'),
                      status: 'ACKNOWLEDGEMENT',
                      data: status.acknowledgementStats?.inProgress ?? 0,
                      color: '#FFA500'
                    },
                    {
                      label: t('ETMF_GRAPHS_LABEL_COMPLETE'),
                      status: 'COMPLETED',
                      data: status.acknowledgementStats?.completed ?? 0,
                      color: '#37BE87'
                    }
                  ],
                  totalData: {
                    data: ackCompl + '%',
                    label: t('COMPLETION'),
                    inGraphLabel: t('COMPLETE')
                  }
                }
              case EOS_ACTIVITY.SUMMARY:
                return {
                  infoData: [
                    {
                      label: t('DOWNLOAD_COMPLETION'),
                      type: EOS_ACTIVITY.DOWNLOAD,
                      data: downloadCompl + '%'
                    },
                    {
                      label: t('ACKNOWLEDGEMENT_COMPLETION'),
                      type: EOS_ACTIVITY.ACKNOWLEDGEMENT,
                      data: ackCompl + '%'
                    }
                  ] as EosGraphInfoItem[]
                }
              default:
                return null
            }
          })()

          setData((prev) => ({ ...prev, [level]: graphData }))
          updateLevelData(level, graphData)
        }
      } catch (error) {
        console.error(`[EosOverview] loadEosStatus error for ${level}:`, error)
      }
    } else {
      setData((prev) => ({ ...prev, [level]: null }))
      updateLevelData(level, null)
    }
  }

  const updateLevelData = (level: string, graphData: EosGraphData | null) => {
    switch (level) {
      case 'studyLevel':
        setStudyLevelData(graphData)
        break
      case 'countryLevel':
        setCountryLevelData(graphData)
        break
      case 'siteLevel':
        setSiteLevelData(graphData)
        break
    }
  }

  // ---------------------------------------------------------------------------
  // Cache
  // ---------------------------------------------------------------------------

  const storeCache = (filterData: EosFilterState = cloneDeep(filter)) => {
    const cacheData: EosCacheData = {
      version: versionName,
      filter: cloneDeep(filterData)
    }
    persistService.setBasic(LOCAL_STORAGE_KEY, cacheData)
  }

  // ---------------------------------------------------------------------------
  // Graph view toggle (Summary ↔ Detailed)
  // ---------------------------------------------------------------------------

  const onGraphViewChange = (level: string, filterData: EosFilterState = cloneDeep(filter)) => {
    loadEosStatus(level, true, filterData).then(() => storeCache(filterData))
  }

  const toggleGraphView = (filterName: string) => {
    const filterData = cloneDeep(filter)
    const sectionFilter = filterData[filterName as keyof EosFilterState]
    sectionFilter.activity =
      sectionFilter.activity === EOS_ACTIVITY.DETAILED ? EOS_ACTIVITY.SUMMARY : EOS_ACTIVITY.DETAILED
    setFilter(filterData)
    onGraphViewChange(filterName, filterData)
  }

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  const goToFolder = (type: string, sectionFilter: EosStatusFilter) => {
    const fsiId = (() => {
      switch (type) {
        case 'STUDY':
          return sectionFilter.study?.value
        case 'SITE':
          return sectionFilter.site?.value
        default:
          return null
      }
    })()

    if (fsiId) {
      navigate(`/browser/${fsiId}`)
    } else {
      context.service.toast.warning(t('ETMF_MSG_ERROR_GET_CLINICAL_FOLDER'))
    }
  }

  // ---------------------------------------------------------------------------
  // Filter change handler
  // ---------------------------------------------------------------------------

  const onFilterChange = async (type: string, name: string, filterData: EosFilterState = cloneDeep(filter)) => {
    try {
      const updatedFilter = await reloadSection(type, name, true, false, filterData)
      await loadEosStatus(type, false, updatedFilter)
      storeCache(updatedFilter)
    } catch (error) {
      console.error('[EosOverview] onFilterChange error:', error)
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="mcc-content-frame theme-sandman eos-study-overview-wrapper">
      <div id="etmf-wrapper">
        <div className="sub-page-header">
          <h1 className="align-left align-top mb10">{t('EOS_OVERVIEW_TITLE')}</h1>
        </div>
        <div id="etmfStatusPage-content">
          <div className="row d-flex align-items-stretch">
            {/* ============ STUDY LEVEL ============ */}
            <div className="col-xs-4" id="section-studyLevel">
              <div className="graph-wrapper">
                <h3>{t('ETMF_GRAPHS_TITLE_STUDY_LEVEL')}</h3>
                {!isLegacy && (
                  <EosStatusGraphToggle
                    toggleGraphView={toggleGraphView}
                    filter={filter.studyLevel}
                    filterName="studyLevel"
                  />
                )}
                <EosStatusGraph
                  data={studyLevelData}
                  filter={filter.studyLevel}
                  isLegacy={isLegacy}
                  onSummaryClick={onGraphSummaryClick}
                  onStatsClick={onGraphLabelClick}
                />

                <div
                  className={`form-group w100p mb7 ${
                    !filteredSelectsOptions.studyLevel.study?.length ? 'disabled' : ''
                  }`}
                >
                  <ControlLabel className="with-asterisk">{t('EMTF_MD_TRIAL')}</ControlLabel>
                  <div className="select-with-btn-wrapper">
                    <Select
                      className="study-select"
                      value={filter.studyLevel.study?.value ?? undefined}
                      name="study_study"
                      placeholder={t('SELECT_STUDY')}
                      isDisabled={!filteredSelectsOptions.studyLevel.study?.length}
                      onChange={(option: any) => {
                        const filterData = cloneDeep(filter)
                        filterData.studyLevel.study = option
                        setFilter(filterData)
                        onFilterChange('studyLevel', 'study', filterData)
                      }}
                      options={filteredSelectsOptions.studyLevel.study}
                      searchable
                    />
                    <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={popoverReference}>
                      <button
                        className={`eos-study-overview goto-folder ${!filter.studyLevel.study ? 'disabled' : ''}`}
                        onClick={() => goToFolder('STUDY', filter.studyLevel)}
                        disabled={!filter.studyLevel.study}
                      ></button>
                    </OverlayTrigger>
                  </div>
                </div>

                {isLegacy && (
                  <div className={`form-group w100p mb0 ${!filter.studyLevel.study ? 'disabled' : ''}`}>
                    <ControlLabel>{t('ACTIVITY_TITLE')}</ControlLabel>
                    <Select
                      className="activity-select"
                      value={filter.studyLevel.activity}
                      name="study_activity"
                      placeholder={t('SELECT_ACTIVITY_OPTION')}
                      isDisabled={!filter.studyLevel.study}
                      onChange={(option: any) => {
                        const filterData = cloneDeep(filter)
                        filterData.studyLevel.activity = option?.value as string
                        setFilter(filterData)
                        onFilterChange('studyLevel', 'activity', filterData)
                      }}
                      options={activities}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* ============ COUNTRY LEVEL ============ */}
            <div className="col-xs-4" id="section-countryLevel">
              <div className="graph-wrapper">
                <h3>{t('ETMF_GRAPHS_TITLE_COUNTRY_LEVEL')}</h3>
                {!isLegacy && (
                  <EosStatusGraphToggle
                    toggleGraphView={toggleGraphView}
                    filter={filter.countryLevel}
                    filterName="countryLevel"
                  />
                )}
                <EosStatusGraph
                  data={countryLevelData}
                  filter={filter.countryLevel}
                  isLegacy={isLegacy}
                  onSummaryClick={onGraphSummaryClick}
                  onStatsClick={onGraphLabelClick}
                />

                <div
                  className={`form-group w100p mb7 ${
                    !filteredSelectsOptions.countryLevel.study?.length ? 'disabled' : ''
                  }`}
                >
                  <ControlLabel className="with-asterisk">{t('EMTF_MD_TRIAL')}</ControlLabel>
                  <Select
                    className="study-select"
                    value={filter.countryLevel.study?.value ?? undefined}
                    name="country_study"
                    placeholder={t('SELECT_STUDY')}
                    isDisabled={!filteredSelectsOptions.countryLevel.study?.length}
                    onChange={(option: any) => {
                      const filterData = cloneDeep(filter)
                      filterData.countryLevel.study = option
                      filterData.countryLevel.country = null
                      setFilter(filterData)
                      onFilterChange('countryLevel', 'study', filterData)
                    }}
                    options={filteredSelectsOptions.countryLevel.study}
                    searchable
                  />
                </div>

                <div
                  className={`form-group w100p mb7 ${
                    !filteredSelectsOptions.countryLevel.study?.length ||
                    !filteredSelectsOptions.countryLevel.country?.length
                      ? 'disabled'
                      : ''
                  }`}
                >
                  <ControlLabel className="with-asterisk">{t('EMTF_MD_COUNTRY')}</ControlLabel>
                  <Select
                    className="country-select"
                    value={filter.countryLevel.country?.value !== undefined ? filter.countryLevel.country.value : 0}
                    name="country_country"
                    placeholder={t('ETMF_LABEL_SELECT_COUNTRY')}
                    isDisabled={!filter.countryLevel.study || !filteredSelectsOptions.countryLevel.country?.length}
                    onChange={(option: any) => {
                      const filterData = cloneDeep(filter)
                      filterData.countryLevel.country = option
                      setFilter(filterData)
                      onFilterChange('countryLevel', 'country', filterData)
                    }}
                    options={filteredSelectsOptions.countryLevel.country}
                    searchable
                  />
                </div>

                {isLegacy && (
                  <div className={`form-group w100p mb0 ${filter.countryLevel.country ? 'disabled' : ''}`}>
                    <ControlLabel>{t('ACTIVITY_TITLE')}</ControlLabel>
                    <Select
                      className="activity-select"
                      value={filter.countryLevel.activity}
                      name="country_activity"
                      placeholder={t('SELECT_ACTIVITY_OPTION')}
                      isDisabled={!filter.countryLevel.country}
                      onChange={(option: any) => {
                        const filterData = cloneDeep(filter)
                        filterData.countryLevel.activity = option?.value as string
                        setFilter(filterData)
                        onFilterChange('countryLevel', 'activity', filterData)
                      }}
                      options={activities}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* ============ SITE LEVEL ============ */}
            <div className="col-xs-4" id="section-siteLevel">
              <div className="graph-wrapper">
                <h3>{t('ETMF_GRAPHS_TITLE_SITE_LEVEL')}</h3>
                {!isLegacy && (
                  <EosStatusGraphToggle
                    toggleGraphView={toggleGraphView}
                    filter={filter.siteLevel}
                    filterName="siteLevel"
                  />
                )}
                <EosStatusGraph
                  data={siteLevelData}
                  filter={filter.siteLevel}
                  isLegacy={isLegacy}
                  onSummaryClick={onGraphSummaryClick}
                  onStatsClick={onGraphLabelClick}
                />

                <div
                  className={`form-group w100p mb7 ${
                    !filteredSelectsOptions.siteLevel.study?.length ? 'disabled' : ''
                  }`}
                >
                  <ControlLabel className="with-asterisk">{t('EMTF_MD_TRIAL')}</ControlLabel>
                  <Select
                    className="study-select"
                    value={filter.siteLevel.study?.value ?? undefined}
                    name="site_study"
                    placeholder={t('SELECT_STUDY')}
                    isDisabled={!filteredSelectsOptions.siteLevel.study?.length}
                    onChange={(option: any) => {
                      const filterData = cloneDeep(filter)
                      filterData.siteLevel.study = option
                      filterData.siteLevel.country = null
                      filterData.siteLevel.site = null
                      setFilter(filterData)
                      onFilterChange('siteLevel', 'study', filterData)
                    }}
                    options={filteredSelectsOptions.siteLevel.study}
                    searchable
                  />
                </div>

                <div
                  className={`form-group w100p mb7 ${
                    !filteredSelectsOptions.siteLevel.study?.length || !filteredSelectsOptions.siteLevel.country?.length
                      ? 'disabled'
                      : ''
                  }`}
                >
                  <ControlLabel className="with-asterisk">{t('EMTF_MD_COUNTRY')}</ControlLabel>
                  <Select
                    className="country-select"
                    value={filter.siteLevel.country?.value !== undefined ? filter.siteLevel.country.value : 0}
                    name="country_site"
                    placeholder={t('ETMF_LABEL_SELECT_COUNTRY')}
                    isDisabled={!filter.siteLevel.study || !filteredSelectsOptions.siteLevel.country?.length}
                    onChange={(option: any) => {
                      const filterData = cloneDeep(filter)
                      filterData.siteLevel.country = option
                      filterData.siteLevel.site = null
                      setFilter(filterData)
                      onFilterChange('siteLevel', 'country', filterData)
                    }}
                    options={filteredSelectsOptions.siteLevel.country}
                    searchable
                  />
                </div>

                <div
                  className={`form-group w100p mb7 ${
                    !filteredSelectsOptions.siteLevel.country || !filteredSelectsOptions.siteLevel.site?.length
                      ? 'disabled'
                      : ''
                  }`}
                >
                  <ControlLabel className="with-asterisk">{t('EMTF_MD_SITE')}</ControlLabel>
                  <div className="select-with-btn-wrapper">
                    <Select
                      className="site-select"
                      value={filter.siteLevel.site?.value !== undefined ? filter.siteLevel.site.value : 0}
                      name="site_site"
                      placeholder={t('SELECT_SITE')}
                      isDisabled={!(filteredSelectsOptions.siteLevel.site?.length || filter.siteLevel.country)}
                      onChange={(option: any) => {
                        const filterData = cloneDeep(filter)
                        filterData.siteLevel.site = option
                        setFilter(filterData)
                        onFilterChange('siteLevel', 'site', filterData)
                      }}
                      options={filteredSelectsOptions.siteLevel.site}
                      searchable
                    />
                    <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={popoverReference}>
                      <button
                        className={`eos-study-overview goto-folder ${!filter.siteLevel.site?.value ? 'disabled' : ''}`}
                        onClick={() => goToFolder('SITE', filter.siteLevel)}
                        disabled={!filter.siteLevel.site || !filter.siteLevel.site.value}
                      ></button>
                    </OverlayTrigger>
                  </div>
                </div>

                {isLegacy && (
                  <div className={`form-group w100p mb0 ${filter.siteLevel.site ? 'disabled' : ''}`}>
                    <ControlLabel>{t('ACTIVITY_TITLE')}</ControlLabel>
                    <Select
                      className="activity-select"
                      value={filter.siteLevel.activity}
                      name="site_activity"
                      placeholder={t('SELECT_ACTIVITY_OPTION')}
                      isDisabled={!filter.siteLevel.site}
                      onChange={(option: any) => {
                        const filterData = cloneDeep(filter)
                        filterData.siteLevel.activity = option?.value as string
                        setFilter(filterData)
                        onFilterChange('siteLevel', 'activity', filterData)
                      }}
                      options={activities}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ============ STATUS DOCUMENTS TABLE ============ */}
          <EosStatusDocuments filter={eosDocumentsFilter} isLegacy={isLegacy} activitiesOptions={activities} />
        </div>
      </div>
    </div>
  )
}

export default EosOverview
